import { Component, h } from 'preact'
import axios, {AxiosResponse} from 'axios'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '../../modal'
import InputGroup from '../../ui-components/InputGroup'
import ButtonControl from '../../button-control'
import DropdownControl from "../../dropdown/DropdownControl"
import {IdeaStatus, Topic} from '../../topic-moderation/interfaces'
import {TranslationCategory} from "../../../libs/translationsUtils/translations";
import globals from "../../../utils/globals";

type Props = {
  fromIdea: Topic,
  fromIdeaStatus: IdeaStatus,
  baseApiUrl: string,
  ideaStatuses: Array<IdeaStatus>,
  isOpen: boolean,
  onClose: () => void,
  onIdeaMergedSuccess: () => void,
  fetchPhrases: (phrasesSet: Array<{ module: TranslationCategory, key: string }>) => { [key: string]: string; },
  buildPill:(ideaStatus: IdeaStatus) => string
}

interface State {
  toIdeaId: number|null,
  toIdea: Topic|null,
  toIdeaUrl: string,
  nextFromIdeaStatus: IdeaStatus|null,
  acknowledgement: boolean,
  errors: {
    [error: string]: string
  },
  isSubmitting: boolean
}

class MergeIdeaModal extends Component<Props, State> {
  private form: HTMLFormElement | null = null
  constructor (props: Props) {
    super(props)
    this.state = {
      toIdeaId: null,
      toIdea:null,
      toIdeaUrl: '',
      nextFromIdeaStatus: null,
      acknowledgement: false,
      errors: {
        toIdeaUrl: '',
        acknowledgement: ''
      },
      isSubmitting: false
    }
  }

  validate = () => {
    let errors = {}
    const { toIdeaUrl, acknowledgement, nextFromIdeaStatus } = this.state

    if (!toIdeaUrl) {
      Object.assign(errors, { toIdeaUrl: 'Please add a topic URL' })
    }
    if (!acknowledgement) {
      Object.assign(errors, { acknowledgement: 'Please confirm that you understand the ramifications of this action' })
    }
    if(!nextFromIdeaStatus) {
      Object.assign(errors, { nextFromIdeaStatus: 'Please select an ideation status' })
    }
    if (Object.keys(errors).length > 0) {
      this.setState({isSubmitting: false})
      this.setState({ errors })
      throw new Error()
    }
  }

  onMergeIdea = async (e: Event) => {
    e.preventDefault()
    try {
      this.setState({isSubmitting: true})
      this.validate()
      await axios.post(`${this.props.baseApiUrl}/${this.props.fromIdea.id}/mergeVotes`, {
        toIdeaId: this.state.toIdeaId
      })

      await axios.post(`${this.props.baseApiUrl}/${this.props.fromIdea.id}/assignIdeaStatus`,
          {ideaStatusId: this.state.nextFromIdeaStatus?.id})

      await this.sendAutoReplies()
      this.props.onIdeaMergedSuccess()
    } catch (e) { }
  }

  sendAutoReplies = async () => {
    const phrases = await this.props.fetchPhrases([
      {module: 'Common', key: 'reply.idea.status.changed'},
      {module: 'Control', key: 'idea.merge.from'},
      {module: 'Control', key: 'idea.merge.from.votes'},
      {module: 'Control', key: 'idea.merge.to'},
      {module: 'Control', key: 'idea.merge.to.votes'}
    ])
    await axios.get(`${this.props.baseApiUrl}/${this.state.toIdeaId}`)
        .then((response: AxiosResponse) => this.setState({toIdea: response.data}))

    let statusChange = '';
    const nextStatus = this.state.nextFromIdeaStatus as IdeaStatus;
    if (this.props.fromIdeaStatus.id !== nextStatus.id) {
      const statusPill = this.props.buildPill(this.props.fromIdeaStatus)
      const nextStatusPill = this.props.buildPill(this.state.nextFromIdeaStatus as IdeaStatus)
      statusChange = `${phrases['reply.idea.status.changed']} ${statusPill} → ${nextStatusPill}<br>`;
    }

    const fromTag = `<oembed url='${this.ideaUrl(this.props.fromIdea)}'></oembed>`
    const toTag = `<oembed url='${this.ideaUrl(this.state.toIdea as Topic)}'></oembed>`

    const fromReply = `${statusChange}${phrases['idea.merge.from']}<br>${toTag}<br>${phrases['idea.merge.from.votes']}`
    const toReply = `${phrases['idea.merge.to']}<br>${fromTag}<br>${phrases['idea.merge.to.votes']}`

    await axios.post(this.replyApiUrl(this.props.fromIdea.id), {
      content: fromReply
    })
    await axios.post(this.replyApiUrl(this.state.toIdeaId), {
      content: toReply
    })
  }

  ideaUrl = (idea: Topic) => {
    return `${globals('communityUrl')}/ideas/${idea.title.toLowerCase().split(' ').join('-')}-${idea.id}`
  }

  replyApiUrl = (replyToIdeaId: any) => {
    return `${this.props.baseApiUrl}/${replyToIdeaId}/reply`
  }

  onUrlInput = (e: ChangeEvent<HTMLInputElement>) => {
    const toIdeaUrl = e.target.value

    try {
      const url = new URL(toIdeaUrl)
      const splittedPath = url.pathname.split('/')
      const isIdea = splittedPath[1] === 'idea'

      if (!isIdea) throw new Error('Please enter an idea URL')

      const splittedTitle = splittedPath[2].split('-')
      const toIdeaId = parseInt(splittedTitle[splittedTitle.length - 1])

      this.setState(({ errors }) => ({
        toIdeaId,
        toIdeaUrl: url.toString(),
        errors: { ...errors, ...{ toIdeaUrl: '' } }
      }))
    } catch (e) {
      const error: Error = e
      const errorMessage = error instanceof TypeError ? 'Please enter a valid URL' : error.message
      this.setState(({ errors }) => ({
        toIdeaId: null,
        toIdeaUrl: '',
        errors: { ...errors, ...{ toIdeaUrl: errorMessage } }
      }))
    }
  }

  render ({ isOpen, onClose }: Props, { toIdeaUrl, acknowledgement, errors }: State) {
    return <Modal isOpen={isOpen} size='medium'>
      <ModalHeader title='Merge idea' onClose={() => onClose()} />
      <ModalBody>
        <div class='merge-idea-modal'>
          <form ref={(form: HTMLFormElement) => this.form = form} onSubmit={this.onMergeIdea}>
          <InputGroup
            value={toIdeaUrl}
            title='Merge current idea into'
            type='text'
            placeholder='Enter a topic URL'
            onChange={this.onUrlInput}
            error={errors.toIdeaUrl || undefined}
          />
          <InputGroup title={'Ideation status after merging'} error={errors.nextFromIdeaStatus || undefined} customInput={
              <DropdownControl
                  align='adjust'
                  triggerClass='dropdown-wrapper'
                  trigger={
                    <ButtonControl
                        icon='arrowDropdown'
                        title={`${this.state.nextFromIdeaStatus ? this.state.nextFromIdeaStatus.name : 'Select an ideation status'}`}
                    />
                  }
                  items={this.props.ideaStatuses
                      .filter((ideaStatus: IdeaStatus) => ideaStatus.visible)
                      .map((ideaStatus: IdeaStatus) => (
                          {
                            title: ideaStatus.name,
                            value: ideaStatus.id,
                            onClick: () => { this.setState({nextFromIdeaStatus: ideaStatus})},
                          }
                      ))}
              />
            } />
                <InputGroup
            inline
            title='By merging this idea, votes in the current idea will be transferred to the other idea. This cannot be undone.'
            type='checkbox'
            onChangeCheckbox={(e: ChangeEvent<HTMLInputElement>) => this.setState(({ errors }) => ({
              acknowledgement: e.target.checked,
              errors: { ...errors, ...{ acknowledgement: e.target.checked ? '' : 'Please confirm that you understand the ramifications of this action' } }
            }))}
            checked={acknowledgement}
            error={errors.acknowledgement || undefined}
          />
          </form>
        </div>
      </ModalBody>
      <ModalFooter>
        <ButtonControl disabled={this.state.isSubmitting} isLoading={this.state.isSubmitting} title='Merge' variant='primary' onClick={() => this.form?.requestSubmit()} class='qa-merge-idea' />
        <ButtonControl disabled={this.state.isSubmitting} title='Cancel' onClick={() => onClose()} />
      </ModalFooter>
    </Modal>
  }
}

export default MergeIdeaModal
